import { Box, Text } from '@mantine/core'
import { useTranslation } from 'react-i18next'
import { type FormikErrors, type FormikProps, type FormikTouched } from 'formik'
import { type FocusEvent } from 'react'

import type { LogVolunteeringActivityFormFields } from '../LogVolunteeringActivity.types'
import { DateTimeFieldSet } from '../LogVolunteeringActivityForm/DateTimeFieldSet/DateTimeFieldSet'
import { WorkingHoursCheckFieldSet } from '../LogVolunteeringActivityForm/WorkingHoursCheckFieldSet/WorkingHoursCheckFieldSet'
import classes from '../LogVolunteeringActivityForm/LogVolunteeringActivityForm.module.scss'

type DateTimeSectionProps = Readonly<{
  values: LogVolunteeringActivityFormFields
  touched: FormikTouched<LogVolunteeringActivityFormFields>
  errors: FormikErrors<LogVolunteeringActivityFormFields>
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean
  ) => Promise<void | FormikErrors<LogVolunteeringActivityFormFields>>
  setFieldTouched: (
    field: string,
    touched?: boolean | undefined,
    shouldValidate?: boolean | undefined
  ) => Promise<void | FormikErrors<LogVolunteeringActivityFormFields>>
  handleBlur: <A = HTMLInputElement>(e: FocusEvent<A>) => void
  formikInstance: FormikProps<LogVolunteeringActivityFormFields>
  allowMultipleEntries: boolean
  duplicateDates?: string[]
  showWorkingHoursCheck: boolean
  workingHoursRequired: boolean
  showOverlapWarning?: boolean
  showRewardPreview?: boolean
}>

export function DateTimeSection({
  values,
  touched,
  errors,
  setFieldValue,
  setFieldTouched,
  handleBlur,
  formikInstance,
  allowMultipleEntries,
  duplicateDates,
  showWorkingHoursCheck,
  workingHoursRequired,
  showOverlapWarning,
  showRewardPreview
}: DateTimeSectionProps) {
  const { t } = useTranslation()

  return (
    <Box>
      <Text className={classes.SectionTitle}>{t('workplace_giving.volunteering.activityForm.dateAndTime')}</Text>
      <DateTimeFieldSet
        values={values}
        touched={touched}
        errors={errors}
        setFieldValue={setFieldValue}
        setFieldTouched={setFieldTouched}
        handleBlur={handleBlur}
        formikInstance={formikInstance}
        allowMultipleEntries={allowMultipleEntries}
        duplicateDates={duplicateDates}
        showOverlapWarning={showOverlapWarning}
        showRewardPreview={showRewardPreview}
      />
      {showWorkingHoursCheck ? (
        <WorkingHoursCheckFieldSet
          values={values}
          touched={touched}
          errors={errors}
          setFieldValue={setFieldValue}
          setFieldTouched={setFieldTouched}
          handleBlur={handleBlur}
          required={workingHoursRequired}
        />
      ) : null}
    </Box>
  )
}
